/**
 * useEndSequence - Choreographierte Endsequenz für Randalaise
 *
 * Orchestriert die dramatische Schlussanimation wenn die letzte Karte gespielt wird:
 * 1. Karte bleibt sichtbar (cardLinger, 4s)
 * 2. Finish-Splashes nacheinander (finishSplash, 5s pro Spieler)
 * 3. Pause vor Round End (preRoundEnd, 2s)
 * 4. Round End Screen erscheint (complete)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { END_SEQUENCE } from '../config/animationTimings';

// Sequenz-Schritte
export const SEQUENCE_STEPS = {
  IDLE: 'idle',
  CARD_LINGER: 'cardLinger',
  FINISH_SPLASH: 'finishSplash',
  PRE_ROUND_END: 'preRoundEnd',
  COMPLETE: 'complete',
};

// Step durations
const STEP_DURATIONS = {
  [SEQUENCE_STEPS.CARD_LINGER]: END_SEQUENCE.cardLinger,
  [SEQUENCE_STEPS.FINISH_SPLASH]: END_SEQUENCE.finishSplash,
  [SEQUENCE_STEPS.PRE_ROUND_END]: END_SEQUENCE.preRoundEnd,
};

// Step transitions
const NEXT_STEP = {
  [SEQUENCE_STEPS.CARD_LINGER]: SEQUENCE_STEPS.FINISH_SPLASH,
  [SEQUENCE_STEPS.FINISH_SPLASH]: null, // handled specially
  [SEQUENCE_STEPS.PRE_ROUND_END]: SEQUENCE_STEPS.COMPLETE,
};

/**
 * Hook für die choreographierte Endsequenz
 *
 * Verwendet Refs für Timer um React-Lifecycle-Probleme zu vermeiden.
 */
export function useEndSequence({ onSequenceComplete } = {}) {
  // Sequenz State
  const [step, setStep] = useState(SEQUENCE_STEPS.IDLE);
  const [currentFinish, setCurrentFinish] = useState(null);

  // Refs für Daten die sich während der Sequenz nicht ändern sollten
  const finishersRef = useRef([]);
  const finisherIndexRef = useRef(0);
  const timerRef = useRef(null);
  const onCompleteRef = useRef(onSequenceComplete);
  const isRunningRef = useRef(false);

  // Update callback ref
  useEffect(() => {
    onCompleteRef.current = onSequenceComplete;
  }, [onSequenceComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  /**
   * Schedule next step transition
   */
  const scheduleNextStep = useCallback((currentStep) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const duration = STEP_DURATIONS[currentStep];
    if (!duration) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (!isRunningRef.current) return;

      // Handle FINISH_SPLASH specially - it cycles through finishers
      if (currentStep === SEQUENCE_STEPS.FINISH_SPLASH) {
        finisherIndexRef.current++;

        if (finisherIndexRef.current < finishersRef.current.length) {
          // Show next finisher
          const nextFinisher = finishersRef.current[finisherIndexRef.current];
          setCurrentFinish(nextFinisher);
          scheduleNextStep(SEQUENCE_STEPS.FINISH_SPLASH);
        } else {
          // All finishers shown, move to PRE_ROUND_END
          setCurrentFinish(null);
          setStep(SEQUENCE_STEPS.PRE_ROUND_END);
          scheduleNextStep(SEQUENCE_STEPS.PRE_ROUND_END);
        }
        return;
      }

      // Normal step transition
      const nextStep = NEXT_STEP[currentStep];

      if (nextStep === SEQUENCE_STEPS.COMPLETE) {
        isRunningRef.current = false;
        setStep(SEQUENCE_STEPS.COMPLETE);
        setCurrentFinish(null);
        finishersRef.current = [];
        finisherIndexRef.current = 0;

        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
        return;
      }

      if (nextStep === SEQUENCE_STEPS.FINISH_SPLASH) {
        // Start showing finishers
        finisherIndexRef.current = 0;
        const firstFinisher = finishersRef.current[0];

        if (firstFinisher) {
          setCurrentFinish(firstFinisher);
          setStep(SEQUENCE_STEPS.FINISH_SPLASH);
          scheduleNextStep(SEQUENCE_STEPS.FINISH_SPLASH);
        } else {
          // No finishers, skip to PRE_ROUND_END
          setStep(SEQUENCE_STEPS.PRE_ROUND_END);
          scheduleNextStep(SEQUENCE_STEPS.PRE_ROUND_END);
        }
        return;
      }

      // Default: move to next step
      setStep(nextStep);
      scheduleNextStep(nextStep);

    }, duration);
  }, []);

  /**
   * Startet die choreographierte Endsequenz
   * @param {Object} _card - Unused, kept for API compatibility
   * @param {Array} finisherList - List of finishers to show
   */
  const startEndSequence = useCallback((_card, finisherList) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Set refs
    finishersRef.current = finisherList || [];
    finisherIndexRef.current = 0;
    isRunningRef.current = true;

    // Set state
    setCurrentFinish(null);
    setStep(SEQUENCE_STEPS.CARD_LINGER);

    // Schedule first transition
    scheduleNextStep(SEQUENCE_STEPS.CARD_LINGER);
  }, [scheduleNextStep]);

  /**
   * Setzt die Sequenz zurück
   */
  const resetSequence = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    isRunningRef.current = false;
    finishersRef.current = [];
    finisherIndexRef.current = 0;

    setStep(SEQUENCE_STEPS.IDLE);
    setCurrentFinish(null);
  }, []);

  // Derived states
  const isSequenceActive = step !== SEQUENCE_STEPS.IDLE && step !== SEQUENCE_STEPS.COMPLETE;
  const showFinishSplash = step === SEQUENCE_STEPS.FINISH_SPLASH && currentFinish !== null;

  return {
    // Current state
    currentFinish,

    // Derived states
    isSequenceActive,
    showFinishSplash,

    // Actions
    startEndSequence,
    resetSequence,
  };
}

export default useEndSequence;
